import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Trash2, X, AlertCircle, Edit3, Check, Calendar, Repeat,
  Layers, ListChecks, Video, Upload, Link as LinkIcon, ChevronDown, ChevronUp, Copy, GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  violet: { sidebar: "border-violet-500/40 bg-violet-500/10 text-violet-200", column: "border-violet-500/30" },
  orange: { sidebar: "border-orange-500/40 bg-orange-500/10 text-orange-200", column: "border-orange-500/30" },
  sky: { sidebar: "border-sky-500/40 bg-sky-500/10 text-sky-200", column: "border-sky-500/30" },
  amber: { sidebar: "border-amber-500/40 bg-amber-500/10 text-amber-200", column: "border-amber-500/30" },
  rose: { sidebar: "border-rose-500/40 bg-rose-500/10 text-rose-200", column: "border-rose-500/30" },
  emerald: { sidebar: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200", column: "border-emerald-500/30" },
};
const COLOR_KEYS = Object.keys(COLORS);

const WEEK_DAYS = [
  { v: 0, l: "Lu" }, { v: 1, l: "Ma" }, { v: 2, l: "Mi" }, { v: 3, l: "Jo" },
  { v: 4, l: "Vi" }, { v: 5, l: "Sâ" }, { v: 6, l: "Du" },
];

const REC_LABELS = {
  none: null, daily: "Zilnic", weekly: "Săptămânal", biweekly: "Bi-săptămânal", monthly: "Lunar",
};

function recurrenceLabel(rec) {
  if (!rec || rec.type === "none" || !rec.type) return null;
  let txt = REC_LABELS[rec.type];
  if (rec.type === "weekly" || rec.type === "biweekly") {
    const days = (rec.days_of_week || []).map((d) => WEEK_DAYS.find((w) => w.v === d)?.l).filter(Boolean);
    if (days.length) txt += ` (${days.join(", ")})`;
  }
  if (rec.type === "monthly" && rec.day_of_month) txt += ` (ziua ${rec.day_of_month})`;
  return txt;
}

// ===== Reusable components =====
function Modal({ open, onClose, title, children, size = "md" }) {
  if (!open) return null;
  const maxW = { sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg", xl: "max-w-2xl" }[size];
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className={`w-full ${maxW} max-h-[90vh] overflow-y-auto rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-800 bg-zinc-950/95 px-5 py-3 backdrop-blur">
          <h3 className="text-base font-semibold text-zinc-100">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {COLOR_KEYS.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)}
          className={`h-8 w-8 rounded-full border-2 ${COLORS[c].sidebar} ${value === c ? "ring-2 ring-white/40" : "border-transparent"}`} />
      ))}
    </div>
  );
}

// ===== Boards bar (orizontal, sus) =====
function BoardSidebar({ boards, selectedId, onSelect, onAdd, onEdit, onDelete }) {
  return (
    <div data-testid="boards-sidebar" className="flex items-center gap-2 overflow-x-auto border-b border-zinc-800 bg-zinc-950/60 px-4 py-2 sm:px-6">
      <Layers className="h-4 w-4 flex-shrink-0 text-zinc-500" />
      {boards.length === 0 && <span className="text-xs text-zinc-500">Niciun board</span>}
      {boards.map((b) => {
        const cls = COLORS[b.color]?.sidebar || COLORS.violet.sidebar;
        const active = b.id === selectedId;
        return (
          <div key={b.id} className={`group flex flex-shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition cursor-pointer ${active ? cls : "border-transparent text-zinc-300 hover:bg-zinc-900"}`}
            data-testid={`board-${b.id}`} onClick={() => onSelect(b.id)}>
            <span className={`h-2 w-2 rounded-full ${COLORS[b.color]?.column?.replace("border-", "bg-").replace("/30", "") || "bg-violet-400"}`} />
            <span className="whitespace-nowrap">{b.name}</span>
            <button onClick={(e) => { e.stopPropagation(); onEdit(b); }} className="hidden group-hover:inline text-zinc-400 hover:text-zinc-100"><Edit3 className="h-3.5 w-3.5" /></button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(b); }} className="hidden group-hover:inline text-zinc-400 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        );
      })}
      <button data-testid="add-board-btn" onClick={onAdd} title="Board nou"
        className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-dashed border-zinc-700 px-2 py-1.5 text-xs text-zinc-400 hover:border-orange-500/40 hover:text-orange-300">
        <Plus className="h-3.5 w-3.5" /> Board
      </button>
    </div>
  );
}

// ===== Task card =====
function TaskCard({ task, onToggle, onOpen, onDuplicate, onDelete, onDragStart, onDragEnd, isDragging }) {
  const rec = recurrenceLabel(task.recurrence);
  const today = new Date().toISOString().slice(0, 10);
  const isToday = task.due_date === today;
  const isPast = task.due_date && task.due_date < today;
  const isFuture = task.due_date && task.due_date > today;

  let dateCls = "text-zinc-500";
  if (isToday) dateCls = "text-sky-300";
  else if (isPast && !task.done) dateCls = "text-rose-400";
  else if (isFuture) dateCls = "text-zinc-500";

  return (
    <div data-testid={`task-${task.id}`}
      draggable
      onDragStart={(e) => onDragStart?.(e, task)}
      onDragEnd={onDragEnd}
      className={`group rounded-xl border bg-zinc-900/60 p-3 transition hover:border-zinc-700 cursor-grab active:cursor-grabbing ${isDragging ? "opacity-30" : ""} ${task.done ? "opacity-60 border-zinc-800" : isPast ? "border-rose-500/40" : "border-zinc-800"}`}>
      <div className="flex items-start gap-2">
        <button data-testid={`toggle-${task.id}`} onClick={(e) => { e.stopPropagation(); onToggle(task); }}
          className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${task.done ? "border-emerald-500 bg-emerald-500" : "border-zinc-600 hover:border-emerald-400"}`}>
          {task.done && <Check className="h-3 w-3 text-white" />}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onOpen(task)}>
          <h4 className={`text-sm font-medium ${task.done ? "line-through text-zinc-500" : "text-zinc-100"}`}>{task.title}</h4>
          {(task.due_date || rec) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {task.due_date && (
                <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${dateCls}`}>
                  <Calendar className="h-3 w-3" /> {task.due_date}
                </span>
              )}
              {rec && (
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-200">
                  <Repeat className="h-2.5 w-2.5" /> {rec}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100">
          <button onClick={(e) => { e.stopPropagation(); onOpen(task); }} title="Editează" className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-zinc-200"><Edit3 className="h-3.5 w-3.5" /></button>
          <button data-testid={`duplicate-${task.id}`} onClick={(e) => { e.stopPropagation(); onDuplicate(task); }} title="Duplică" className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-sky-300"><Copy className="h-3.5 w-3.5" /></button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(task); }} title="Șterge" className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      {/* Link button — visible only when a URL is set inside the task.
          Click opens the link in a new tab (external) or navigates within
          the app if it starts with "/". The link is edited from the task
          form (TaskForm), not via inline prompt. */}
      {task.link && (
        <div className="mt-2 flex items-center justify-end">
          <button
            type="button"
            data-testid={`task-link-${task.id}`}
            onClick={(e) => {
              e.stopPropagation();
              const url = task.link;
              if (!url) return;
              if (url.startsWith("/")) {
                window.open(url, "_blank", "noopener,noreferrer");
              } else {
                window.open(url, "_blank", "noopener,noreferrer");
              }
            }}
            title={`Deschide: ${task.link}`}
            className="inline-flex h-6 items-center gap-1 rounded-md border border-sky-500/50 bg-sky-500/15 px-2 text-[10px] font-medium text-sky-200 transition hover:bg-sky-500/25"
          >
            <LinkIcon className="h-3 w-3" />
            Deschide link
          </button>
        </div>
      )}
    </div>
  );
}

// ===== Task detail modal (view + edit toggle) =====
function isYouTubeUrl(u) { return /youtube\.com\/watch|youtu\.be\//.test(u || ""); }
function isVimeoUrl(u) { return /vimeo\.com\//.test(u || ""); }
function toEmbedUrl(u) {
  if (!u) return null;
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = u.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function VideoPlayer({ url }) {
  if (!url) return null;
  const isYT = isYouTubeUrl(url);
  const isVM = isVimeoUrl(url);
  if (isYT || isVM) {
    const embed = toEmbedUrl(url);
    return (
      <div className="aspect-video w-full overflow-hidden rounded-xl border border-zinc-800 bg-black">
        <iframe src={embed} className="h-full w-full" allow="autoplay; fullscreen; picture-in-picture" title="video" />
      </div>
    );
  }
  // direct video file (uploaded or external link)
  const src = url.startsWith("/api/") ? `${BACKEND}${url}` : url;
  return (
    <video controls className="w-full max-h-96 rounded-xl border border-zinc-800 bg-black">
      <source src={src} />
      Browser-ul nu suportă acest format video.
    </video>
  );
}

function TaskDetailModal({ task, open, onClose, onEdit, onDuplicate, onDelete }) {
  if (!open || !task) return null;
  const rec = recurrenceLabel(task.recurrence);
  const steps = task.steps || [];
  const videoSrc = task.video_url || (task.video_filename ? `/api/uploads/${task.video_filename}` : null);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl sm:rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="task-detail-modal">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-zinc-800 bg-zinc-950/95 px-5 py-3 backdrop-blur">
          <div className="flex-1 min-w-0">
            <h3 className={`text-lg font-semibold ${task.done ? "line-through text-zinc-500" : "text-zinc-100"}`}>{task.title}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {task.due_date && <span className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{task.due_date}</span>}
              {rec && <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-medium text-violet-200"><Repeat className="h-2.5 w-2.5" />{rec}</span>}
            </div>
          </div>
          <div className="flex gap-1">
            <button data-testid="detail-edit-btn" onClick={() => onEdit(task)} title="Editează" className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-orange-300"><Edit3 className="h-4 w-4" /></button>
            <button data-testid="detail-duplicate-btn" onClick={() => onDuplicate(task)} title="Duplică" className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-sky-300"><Copy className="h-4 w-4" /></button>
            <button onClick={() => onDelete(task)} title="Șterge" className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
            <button onClick={onClose} title="Închide" className="rounded-lg p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="space-y-5 px-5 py-4">
          {task.description && (
            <section>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500">Descriere</h4>
              <p className="whitespace-pre-wrap text-sm text-zinc-200">{task.description}</p>
            </section>
          )}

          {steps.length > 0 && (
            <section>
              <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                <ListChecks className="h-3.5 w-3.5" /> Pași de urmat
              </h4>
              <div className="space-y-1.5">
                {steps.map((s, idx) => (
                  <div key={idx} data-testid={`step-${idx}`} className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-[10px] font-semibold text-sky-300">{idx + 1}</span>
                    <span className="flex-1 text-sm text-zinc-200">{s.text}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {task.link && (
            <section>
              <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                <LinkIcon className="h-3.5 w-3.5" /> Link
              </h4>
              <a
                href={task.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-open-link-btn"
                className="inline-flex items-center gap-2 rounded-lg border border-sky-500/50 bg-sky-500/15 px-3 py-2 text-sm font-medium text-sky-200 transition hover:bg-sky-500/25"
              >
                <LinkIcon className="h-4 w-4" />
                <span className="truncate max-w-[420px]">{task.link}</span>
              </a>
            </section>
          )}

          {videoSrc && (
            <section>
              <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                <Video className="h-3.5 w-3.5" /> Video explicativ
              </h4>
              <VideoPlayer url={videoSrc} />
            </section>
          )}

          {!task.description && steps.length === 0 && !videoSrc && !task.link && (
            <p className="py-8 text-center text-sm text-zinc-500">Acest task nu are descriere, pași sau video.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Category column =====
function CategoryColumn({
  category, tasks, onAddTask, onEditCat, onDeleteCat,
  onTaskToggle, onTaskOpen, onTaskDuplicate, onTaskDelete,
  draggingTaskId, onTaskDragStart, onTaskDragEnd, onTaskDropAt, onTaskDropEnd,
  onCatDragStart, onCatDragEnd, onCatDragOver, onCatDrop, catDragOver, isCatDragging,
}) {
  const [showDone, setShowDone] = useState(false);
  const [overIndex, setOverIndex] = useState(-1);
  const cls = COLORS[category.color]?.column || COLORS.sky.column;
  const today = new Date().toISOString().slice(0, 10);
  // Hide auto-generated recurring tasks whose due_date is in the future (they'll appear when their day comes)
  const visible = tasks.filter((t) => !(t.auto_generated && t.due_date && t.due_date > today));
  const open = visible.filter((t) => !t.done);
  const done = visible.filter((t) => t.done);

  const allowDrop = (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; };

  return (
    <div data-testid={`category-${category.id}`}
      draggable={false}
      onDragOver={(e) => onCatDragOver?.(e, category.id)}
      onDrop={(e) => onCatDrop?.(e, category.id)}
      className={`flex w-80 flex-shrink-0 flex-col rounded-2xl border-t-2 ${cls} bg-zinc-950/60 transition ${isCatDragging ? "opacity-40" : ""} ${catDragOver ? "ring-2 ring-orange-500/60" : ""}`}>
      <div
        draggable
        onDragStart={(e) => onCatDragStart?.(e, category)}
        onDragEnd={onCatDragEnd}
        className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-800 cursor-grab active:cursor-grabbing">
        <div className="flex items-center gap-2">
          <GripVertical data-testid={`cat-drag-${category.id}`} className="h-3.5 w-3.5 text-zinc-600" />
          <h3 className="text-sm font-semibold text-zinc-100">{category.name}</h3>
          <span className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-400">{open.length}</span>
        </div>
        <div className="flex gap-1">
          <button onClick={onAddTask} data-testid={`add-task-${category.id}`} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-orange-300"><Plus className="h-3.5 w-3.5" /></button>
          <button onClick={onEditCat} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"><Edit3 className="h-3.5 w-3.5" /></button>
          <button onClick={onDeleteCat} className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <div
        data-testid={`drop-zone-${category.id}`}
        className="flex-1 space-y-2 overflow-y-auto p-2 min-h-[200px]"
        onDragOver={allowDrop}
        onDragLeave={() => setOverIndex(-1)}
        onDrop={(e) => {
          if (!draggingTaskId) return; // category being dragged → let bubble to column onDrop
          e.preventDefault(); e.stopPropagation();
          onTaskDropEnd?.(category.id, overIndex === -1 ? open.length : overIndex);
          setOverIndex(-1);
        }}
      >
        {open.length === 0 && done.length === 0 && (
          <button onClick={onAddTask} className="w-full rounded-lg border border-dashed border-zinc-800 py-6 text-xs text-zinc-500 hover:border-orange-500/40 hover:text-orange-300">
            <Plus className="mx-auto mb-1 h-4 w-4" /> Adaugă primul task
          </button>
        )}
        {open.map((t, idx) => (
          <div key={t.id}
            onDragOver={(e) => { if (draggingTaskId) { e.preventDefault(); e.stopPropagation(); setOverIndex(idx); } }}
            onDrop={(e) => {
              if (!draggingTaskId) return;
              e.preventDefault(); e.stopPropagation();
              onTaskDropEnd?.(category.id, idx);
              setOverIndex(-1);
            }}
            className={overIndex === idx && draggingTaskId && draggingTaskId !== t.id ? "border-t-2 border-orange-400 pt-1 -mt-1" : ""}
          >
            <TaskCard task={t} onToggle={onTaskToggle} onOpen={onTaskOpen} onDuplicate={onTaskDuplicate} onDelete={onTaskDelete}
              onDragStart={onTaskDragStart} onDragEnd={onTaskDragEnd} isDragging={draggingTaskId === t.id} />
          </div>
        ))}
        {done.length > 0 && (
          <div className="pt-3 mt-2 border-t border-zinc-800/60">
            <button data-testid={`toggle-done-${category.id}`} onClick={() => setShowDone((v) => !v)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400 hover:border-zinc-700 hover:text-zinc-200">
              <span className="font-medium uppercase tracking-wider">Finalizate ({done.length})</span>
              {showDone ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
            {showDone && (
              <div className="mt-2 space-y-2">
                {done.map((t) => <TaskCard key={t.id} task={t} onToggle={onTaskToggle} onOpen={onTaskOpen} onDuplicate={onTaskDuplicate} onDelete={onTaskDelete}
                  onDragStart={onTaskDragStart} onDragEnd={onTaskDragEnd} isDragging={draggingTaskId === t.id} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ===== Forms =====
function BoardForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [color, setColor] = useState(initial?.color || "violet");
  return (
    <div className="space-y-3">
      <input data-testid="board-name-input" autoFocus placeholder="Nume board *" value={name} onChange={(e) => setName(e.target.value)}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Culoare</p>
        <ColorPicker value={color} onChange={setColor} />
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={onCancel} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300 hover:bg-zinc-800">Anulează</button>
        <button data-testid="board-save-btn" disabled={!name.trim()} onClick={() => onSave({ name, color })}
          className="flex-1 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">Salvează</button>
      </div>
    </div>
  );
}

function CategoryForm({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [color, setColor] = useState(initial?.color || "sky");
  return (
    <div className="space-y-3">
      <input data-testid="category-name-input" autoFocus placeholder="Nume categorie (Zilnice, Urgente...)" value={name} onChange={(e) => setName(e.target.value)}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Culoare</p>
        <ColorPicker value={color} onChange={setColor} />
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={onCancel} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300 hover:bg-zinc-800">Anulează</button>
        <button data-testid="category-save-btn" disabled={!name.trim()} onClick={() => onSave({ name, color })}
          className="flex-1 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">Salvează</button>
      </div>
    </div>
  );
}

function TaskForm({ initial, categories, onSave, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [dueDate, setDueDate] = useState(initial?.due_date || "");
  const [categoryId, setCategoryId] = useState(initial?.category_id || categories[0]?.id || "");
  const initialRec = initial?.recurrence || { type: "none" };
  const [recType, setRecType] = useState(initialRec.type || "none");
  const [daysOfWeek, setDaysOfWeek] = useState(Array.isArray(initialRec.days_of_week) ? initialRec.days_of_week : []);
  const [dayOfMonth, setDayOfMonth] = useState(initialRec.day_of_month ?? 1);
  const [steps, setSteps] = useState(initial?.steps || []);
  const [videoUrl, setVideoUrl] = useState(initial?.video_url || "");
  const [videoFilename, setVideoFilename] = useState(initial?.video_filename || "");
  const [link, setLink] = useState(initial?.link || "");
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoError, setVideoError] = useState("");
  const fileInputRef = useRef(null);

  const toggleDay = (v) => {
    setDaysOfWeek((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v].sort((a, b) => a - b));
  };

  const addStep = () => setSteps([...steps, { text: "", done: false }]);
  const updateStep = (i, text) => setSteps(steps.map((s, idx) => idx === i ? { ...s, text } : s));
  const removeStep = (i) => setSteps(steps.filter((_, idx) => idx !== i));
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const arr = [...steps]; [arr[i], arr[j]] = [arr[j], arr[i]];
    setSteps(arr);
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setVideoError(""); setVideoUploading(true); setVideoUploadProgress(0);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/uploads/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setVideoUploadProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setVideoFilename(r.data.filename);
      setVideoUrl(""); // clear URL since we have a file now
    } catch (err) {
      setVideoError(err.response?.data?.detail || err.message);
    } finally {
      setVideoUploading(false);
    }
  };

  const clearVideo = async () => {
    if (videoFilename) {
      try { await axios.delete(`${API}/uploads/${videoFilename}`); } catch (e) { /* ignore */ }
    }
    setVideoFilename(""); setVideoUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const save = () => {
    const recurrence = { type: recType };
    if (recType === "weekly" || recType === "biweekly") recurrence.days_of_week = daysOfWeek;
    if (recType === "monthly") recurrence.day_of_month = Number(dayOfMonth);
    const cleanSteps = steps.filter((s) => s.text.trim()).map((s) => ({ text: s.text.trim(), done: !!s.done }));
    onSave({
      title: title.trim(),
      description,
      priority: "medium",
      due_date: dueDate || null,
      category_id: categoryId,
      recurrence,
      steps: cleanSteps,
      video_url: videoUrl.trim() || null,
      video_filename: videoFilename || null,
      link: link.trim() || null,
    });
  };

  return (
    <div className="space-y-4">
      <input data-testid="task-title-input" autoFocus placeholder="Titlu task *" value={title} onChange={(e) => setTitle(e.target.value)}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
      <textarea placeholder="Descriere (opțional - se vede doar la click pe task)" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
        className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Categorie</label>
          <select data-testid="task-category-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Dată scadență</label>
          <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none" />
        </div>
      </div>

      {/* Recurrence */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Repeat className="h-3.5 w-3.5 text-violet-400" />
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Recurență</label>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { v: "none", l: "Fără" },
            { v: "daily", l: "Zilnic" },
            { v: "weekly", l: "Săptămânal" },
            { v: "biweekly", l: "Bi-săptămânal" },
            { v: "monthly", l: "Lunar" },
          ].map((opt) => (
            <button key={opt.v} type="button" data-testid={`rec-${opt.v}`} onClick={() => setRecType(opt.v)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${recType === opt.v ? "border-violet-500/40 bg-violet-500/15 text-violet-200" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700"}`}>
              {opt.l}
            </button>
          ))}
        </div>
        {(recType === "weekly" || recType === "biweekly") && (
          <div className="mt-3">
            <p className="mb-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
              Ziua/zilele săptămânii {recType === "weekly" ? "(poți selecta mai multe)" : "(repetare la 2 săptămâni)"}
            </p>
            <div className="flex flex-wrap gap-1">
              {WEEK_DAYS.map((w) => {
                const sel = daysOfWeek.includes(w.v);
                return (
                  <button key={w.v} type="button" data-testid={`weekday-${w.v}`} onClick={() => toggleDay(w.v)}
                    className={`h-9 w-9 rounded-lg border text-xs font-medium transition ${sel ? "border-orange-500/40 bg-orange-500/15 text-orange-200" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                    {w.l}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {recType === "monthly" && (
          <div className="mt-3">
            <p className="mb-1.5 text-[10px] uppercase tracking-wider text-zinc-500">Ziua lunii (1-31)</p>
            <input type="number" min={1} max={31} value={dayOfMonth} onChange={(e) => setDayOfMonth(e.target.value)}
              className="w-24 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none" />
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-3.5 w-3.5 text-sky-400" />
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Pași de urmat</label>
          </div>
          <button type="button" data-testid="add-step-btn" onClick={addStep}
            className="inline-flex items-center gap-1 rounded-lg border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[10px] font-medium text-sky-200 hover:bg-sky-500/20">
            <Plus className="h-3 w-3" /> Adaugă pas
          </button>
        </div>
        {steps.length === 0 ? (
          <p className="text-xs text-zinc-600">Niciun pas adăugat. Apasă &bdquo;Adaugă pas&rdquo; pentru a crea o listă de pași.</p>
        ) : (
          <div className="space-y-1.5">
            {steps.map((s, i) => (
              <div key={i} data-testid={`step-input-${i}`} className="flex items-center gap-1.5">
                <span className="w-6 text-center text-xs text-zinc-500">{i + 1}.</span>
                <input value={s.text} onChange={(e) => updateStep(i, e.target.value)} placeholder={`Pas ${i + 1}`}
                  className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
                <button type="button" onClick={() => moveStep(i, -1)} disabled={i === 0} className="rounded p-1 text-zinc-500 hover:text-zinc-200 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => moveStep(i, 1)} disabled={i === steps.length - 1} className="rounded p-1 text-zinc-500 hover:text-zinc-200 disabled:opacity-30">↓</button>
                <button type="button" onClick={() => removeStep(i)} className="rounded p-1 text-zinc-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Link (Google Docs URL sau /pagină internă din app) */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
        <div className="mb-2 flex items-center gap-2">
          <LinkIcon className="h-3.5 w-3.5 text-sky-400" />
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Link (opțional)</label>
        </div>
        <input
          data-testid="task-link-input"
          type="text"
          placeholder="https://docs.google.com/... sau /produse"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-sky-500/50"
        />
        <p className="mt-1 text-[10px] text-zinc-600">
          Poți pune un URL extern (Google Docs, drive, articol) sau o cale internă (ex: /produse, /reviews). Se deschide într-un tab nou dintr-un buton pe card.
        </p>
      </div>

      {/* Video */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-3">
        <div className="mb-2 flex items-center gap-2">
          <Video className="h-3.5 w-3.5 text-emerald-400" />
          <label className="text-xs font-semibold uppercase tracking-wider text-zinc-300">Video explicativ (opțional)</label>
        </div>
        {videoFilename ? (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
            <Video className="h-4 w-4" />
            <span className="flex-1 truncate">Video încărcat: <span className="font-mono">{videoFilename}</span></span>
            <button type="button" onClick={clearVideo} className="rounded p-1 hover:bg-emerald-500/20"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : videoUrl ? (
          <div className="flex items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200">
            <LinkIcon className="h-4 w-4" />
            <span className="flex-1 truncate">{videoUrl}</span>
            <button type="button" onClick={() => setVideoUrl("")} className="rounded p-1 hover:bg-sky-500/20"><X className="h-3.5 w-3.5" /></button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button type="button" data-testid="upload-video-btn" onClick={() => fileInputRef.current?.click()} disabled={videoUploading}
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
                <Upload className="h-3.5 w-3.5" /> {videoUploading ? `Upload ${videoUploadProgress}%` : "Încarcă video"}
              </button>
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={uploadVideo} />
              <input data-testid="video-url-input" type="url" placeholder="Sau lipește URL (YouTube, Vimeo, link direct)" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-100 placeholder-zinc-600 outline-none focus:border-orange-500/50" />
            </div>
            {videoUploading && (
              <div className="h-1 w-full overflow-hidden rounded-full bg-zinc-800">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all" style={{ width: `${videoUploadProgress}%` }} />
              </div>
            )}
            {videoError && <p className="text-xs text-rose-300">{videoError}</p>}
            <p className="text-[10px] text-zinc-600">Max 200 MB. Formate: mp4, webm, mov, etc.</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 pt-2 sticky bottom-0 bg-zinc-950 -mx-5 px-5 py-3 border-t border-zinc-800">
        <button onClick={onCancel} className="flex-1 rounded-lg border border-zinc-800 bg-zinc-900 py-2 text-sm text-zinc-300 hover:bg-zinc-800">Anulează</button>
        <button data-testid="task-save-btn" disabled={!title.trim() || !categoryId} onClick={save}
          className="flex-1 rounded-lg bg-gradient-to-b from-orange-500 to-rose-500 py-2 text-sm font-medium text-white disabled:opacity-50">Salvează</button>
      </div>
    </div>
  );
}

// ===== Main page =====
export default function Tasks() {
  const [boards, setBoards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [boardModal, setBoardModal] = useState(null); // null | {} | board
  const [catModal, setCatModal] = useState(null);
  const [taskModal, setTaskModal] = useState(null); // null | {category_id, ...} | task (edit form)
  const [detailTask, setDetailTask] = useState(null); // task being viewed

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, cRes, tRes] = await Promise.all([
        axios.get(`${API}/boards`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/tasks`),
      ]);
      setBoards(bRes.data.boards || []);
      setCategories(cRes.data.categories || []);
      setTasks(tRes.data.tasks || []);
      if (!selectedBoardId && bRes.data.boards?.length) setSelectedBoardId(bRes.data.boards[0].id);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [selectedBoardId]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const selectedBoard = boards.find((b) => b.id === selectedBoardId);
  // Categoriile cu cele mai multe task-uri nerezolvate vin primele (stânga);
  // la egalitate rămâne ordinea manuală.
  const todayStr = new Date().toISOString().slice(0, 10);
  const openCount = (cid) => tasks.filter((t) => t.category_id === cid && !t.done
    && !(t.auto_generated && t.due_date && t.due_date > todayStr)).length;
  const boardCategories = categories.filter((c) => c.board_id === selectedBoardId)
    .sort((a, b) => (openCount(b.id) - openCount(a.id)) || (a.order - b.order));

  // Board actions
  const saveBoard = async (data) => {
    try {
      if (boardModal?.id) {
        const r = await axios.put(`${API}/boards/${boardModal.id}`, data);
        setBoards((prev) => prev.map((b) => b.id === r.data.id ? r.data : b));
      } else {
        const r = await axios.post(`${API}/boards`, data);
        setBoards((prev) => [...prev, r.data]);
        setSelectedBoardId(r.data.id);
      }
      setBoardModal(null);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const deleteBoard = async (b) => {
    if (!window.confirm(`Ștergi board-ul "${b.name}" cu toate categoriile și task-urile sale?`)) return;
    await axios.delete(`${API}/boards/${b.id}`);
    loadAll();
  };

  // Category actions
  const saveCategory = async (data) => {
    try {
      if (catModal?.id) {
        const r = await axios.put(`${API}/categories/${catModal.id}`, data);
        setCategories((prev) => prev.map((c) => c.id === r.data.id ? r.data : c));
      } else {
        const r = await axios.post(`${API}/categories`, { ...data, board_id: selectedBoardId });
        setCategories((prev) => [...prev, r.data]);
      }
      setCatModal(null);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Ștergi categoria "${c.name}" și toate task-urile din ea?`)) return;
    await axios.delete(`${API}/categories/${c.id}`);
    loadAll();
  };

  // Task actions
  const saveTask = async (data) => {
    try {
      if (taskModal?.id) {
        const r = await axios.put(`${API}/tasks/${taskModal.id}`, data);
        setTasks((prev) => prev.map((t) => t.id === r.data.id ? r.data : t));
      } else {
        const r = await axios.post(`${API}/tasks`, { ...data, board_id: selectedBoardId });
        setTasks((prev) => [r.data, ...prev]);
      }
      setTaskModal(null);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const toggleTask = async (t) => {
    try {
      const r = await axios.post(`${API}/tasks/${t.id}/toggle`);
      setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, done: r.data.done, completed_at: r.data.done ? new Date().toISOString() : null } : x));
      if (r.data.next_task) setTasks((prev) => [r.data.next_task, ...prev]);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  // State for the recurring-task delete confirmation modal
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { task } | null

  const deleteTask = async (t) => {
    // Non-recurring: simple confirm (preserves prior UX)
    const isRecurring = (t?.recurrence?.type || "none") !== "none";
    if (!isRecurring) {
      if (!window.confirm("Ștergi acest task?")) return;
      try {
        await axios.delete(`${API}/tasks/${t.id}`);
        setTasks((prev) => prev.filter((x) => x.id !== t.id));
        setDetailTask(null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message);
      }
      return;
    }
    // Recurring: open the dedicated dialog so the user can pick a scope.
    setDeleteConfirm({ task: t });
  };

  const performDelete = async (scope) => {
    const t = deleteConfirm?.task;
    if (!t) return;
    try {
      const r = await axios.delete(`${API}/tasks/${t.id}`, { params: { scope } });
      if (scope === "series") {
        // Remove this task + every sibling that the backend deleted.
        setTasks((prev) =>
          prev.filter((x) => {
            if (x.id === t.id) return false;
            const sameSeries =
              x.board_id === t.board_id &&
              x.category_id === t.category_id &&
              x.title === t.title &&
              (x.recurrence?.type || "none") !== "none" &&
              (!t.due_date || (x.due_date && x.due_date >= t.due_date));
            return !sameSeries;
          })
        );
        toast.success(`Șters task + ${r.data?.siblings_deleted || 0} ocurențe viitoare`);
      } else {
        setTasks((prev) => prev.filter((x) => x.id !== t.id));
        toast.success("Task șters");
      }
      setDetailTask(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const duplicateTask = async (t) => {
    try {
      const r = await axios.post(`${API}/tasks/${t.id}/duplicate`);
      setTasks((prev) => [r.data, ...prev]);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  // ===== Drag & drop =====
  const [draggingTaskId, setDraggingTaskId] = useState(null);
  const [draggingCatId, setDraggingCatId] = useState(null);
  const [catDragOverId, setCatDragOverId] = useState(null);

  const onTaskDragStart = (e, task) => {
    setDraggingTaskId(task.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", task.id);
    }
  };
  const onTaskDragEnd = () => setDraggingTaskId(null);

  const onTaskDropEnd = async (targetCategoryId, insertIndex) => {
    const taskId = draggingTaskId;
    if (!taskId) return;
    setDraggingTaskId(null);
    const dragged = tasks.find((t) => t.id === taskId);
    if (!dragged) return;
    // Build new orders per affected category (only "open" tasks ordering matters; we keep done tasks at the bottom of their category by leaving them untouched)
    const today = new Date().toISOString().slice(0, 10);
    const isOpen = (t) => !t.done && !(t.auto_generated && t.due_date && t.due_date > today);

    // current open list for target
    let targetList = tasks
      .filter((t) => t.category_id === targetCategoryId && t.id !== taskId && isOpen(t))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const clampedIndex = Math.max(0, Math.min(insertIndex, targetList.length));
    const newDragged = { ...dragged, category_id: targetCategoryId };
    targetList = [...targetList.slice(0, clampedIndex), newDragged, ...targetList.slice(clampedIndex)];

    const updates = [];
    targetList.forEach((t, i) => updates.push({ id: t.id, category_id: targetCategoryId, order: i }));

    // If task moved across categories, also re-order source category
    if (dragged.category_id !== targetCategoryId) {
      const srcList = tasks
        .filter((t) => t.category_id === dragged.category_id && t.id !== taskId && isOpen(t))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      srcList.forEach((t, i) => updates.push({ id: t.id, category_id: dragged.category_id, order: i }));
    }

    // Optimistic update
    setTasks((prev) => prev.map((t) => {
      const u = updates.find((x) => x.id === t.id);
      return u ? { ...t, category_id: u.category_id, order: u.order } : t;
    }));

    try {
      await axios.post(`${API}/tasks/reorder`, updates);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      loadAll();
    }
  };

  const onCatDragStart = (e, cat) => {
    // don't start a category drag while a task is being dragged
    if (draggingTaskId) { e.preventDefault?.(); return; }
    setDraggingCatId(cat.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", `cat:${cat.id}`);
    }
  };
  const onCatDragEnd = () => { setDraggingCatId(null); setCatDragOverId(null); };
  const onCatDragOver = (e, catId) => {
    if (!draggingCatId || draggingCatId === catId) return;
    e.preventDefault();
    setCatDragOverId(catId);
  };
  const onCatDrop = async (e, targetCatId) => {
    if (!draggingCatId || draggingCatId === targetCatId) return;
    e.preventDefault();
    e.stopPropagation();
    const ordered = [...boardCategories];
    const fromIdx = ordered.findIndex((c) => c.id === draggingCatId);
    const toIdx = ordered.findIndex((c) => c.id === targetCatId);
    if (fromIdx < 0 || toIdx < 0) return;
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    const updates = ordered.map((c, i) => ({ id: c.id, order: i }));
    setCategories((prev) => prev.map((c) => {
      const u = updates.find((x) => x.id === c.id);
      return u ? { ...c, order: u.order } : c;
    }));
    setDraggingCatId(null); setCatDragOverId(null);
    try {
      await axios.post(`${API}/categories/reorder`, updates);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      loadAll();
    }
  };

  const saveTaskAndRefreshDetail = async (data) => {
    try {
      if (taskModal?.id) {
        const r = await axios.put(`${API}/tasks/${taskModal.id}`, data);
        setTasks((prev) => prev.map((t) => t.id === r.data.id ? r.data : t));
        if (detailTask?.id === r.data.id) setDetailTask(r.data);
      } else {
        const r = await axios.post(`${API}/tasks`, { ...data, board_id: selectedBoardId });
        setTasks((prev) => [r.data, ...prev]);
      }
      setTaskModal(null);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="flex flex-col min-h-[calc(100vh-65px)]" data-testid="tasks-page">
      <BoardSidebar boards={boards} selectedId={selectedBoardId} onSelect={setSelectedBoardId}
        onAdd={() => setBoardModal({})} onEdit={(b) => setBoardModal(b)} onDelete={deleteBoard} />

      <div className="flex-1 overflow-hidden">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3 sm:px-6">
          <div>
            <h1 className="text-xl font-semibold text-zinc-50">{selectedBoard?.name || "Tasks"}</h1>
            <p className="text-xs text-zinc-500">
              {boardCategories.length} {boardCategories.length === 1 ? "categorie" : "categorii"} ·{" "}
              {tasks.filter((t) => t.board_id === selectedBoardId && !t.done).length} task-uri active
            </p>
          </div>
          {selectedBoardId && (
            <button data-testid="add-category-btn" onClick={() => setCatModal({})}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-orange-500 to-rose-500 px-3 py-2 text-xs font-medium text-white hover:from-orange-400 hover:to-rose-400">
              <Plus className="h-3.5 w-3.5" /> Categorie nouă
            </button>
          )}
        </div>

        {error && (
          <div className="m-4 flex items-start gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            <AlertCircle className="mt-0.5 h-4 w-4" /><div className="flex-1">{error}</div>
            <button onClick={() => setError("")}><X className="h-4 w-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="p-8 text-center text-zinc-500">Se încarcă...</div>
        ) : !selectedBoardId ? (
          <div className="p-12 text-center text-zinc-500">
            <Layers className="mx-auto mb-3 h-10 w-10 text-zinc-700" />
            <p>Selectează un board sau creează unul nou.</p>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto p-4 sm:p-6" data-testid="kanban-columns">
            {boardCategories.length === 0 ? (
              <div className="mx-auto py-12 text-center">
                <p className="text-sm text-zinc-400">Acest board nu are categorii.</p>
                <button onClick={() => setCatModal({})} className="mt-3 inline-flex items-center gap-2 rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-2 text-sm text-orange-200">
                  <Plus className="h-4 w-4" /> Adaugă prima categorie
                </button>
              </div>
            ) : (
              boardCategories.map((cat) => (
                <CategoryColumn key={cat.id} category={cat}
                  tasks={tasks.filter((t) => t.category_id === cat.id)}
                  onAddTask={() => setTaskModal({ category_id: cat.id })}
                  onEditCat={() => setCatModal(cat)}
                  onDeleteCat={() => deleteCategory(cat)}
                  onTaskToggle={toggleTask}
                  onTaskOpen={(t) => setDetailTask(t)}
                  onTaskDuplicate={duplicateTask}
                  onTaskDelete={deleteTask}
                  draggingTaskId={draggingTaskId}
                  onTaskDragStart={onTaskDragStart}
                  onTaskDragEnd={onTaskDragEnd}
                  onTaskDropEnd={onTaskDropEnd}
                  onCatDragStart={onCatDragStart}
                  onCatDragEnd={onCatDragEnd}
                  onCatDragOver={onCatDragOver}
                  onCatDrop={onCatDrop}
                  catDragOver={catDragOverId === cat.id}
                  isCatDragging={draggingCatId === cat.id}
                />
              ))
            )}
          </div>
        )}
      </div>

      <Modal open={!!boardModal} onClose={() => setBoardModal(null)} title={boardModal?.id ? "Editează board" : "Board nou"}>
        {boardModal && <BoardForm initial={boardModal} onSave={saveBoard} onCancel={() => setBoardModal(null)} />}
      </Modal>

      <Modal open={!!catModal} onClose={() => setCatModal(null)} title={catModal?.id ? "Editează categorie" : "Categorie nouă"}>
        {catModal && <CategoryForm initial={catModal} onSave={saveCategory} onCancel={() => setCatModal(null)} />}
      </Modal>

      <Modal open={!!taskModal} onClose={() => setTaskModal(null)} title={taskModal?.id ? "Editează task" : "Task nou"} size="xl">
        {taskModal && <TaskForm initial={taskModal} categories={boardCategories} onSave={saveTaskAndRefreshDetail} onCancel={() => setTaskModal(null)} />}
      </Modal>

      <TaskDetailModal task={detailTask} open={!!detailTask} onClose={() => setDetailTask(null)}
        onEdit={(t) => { setDetailTask(null); setTaskModal(t); }}
        onDuplicate={duplicateTask}
        onDelete={deleteTask} />

      {/* Confirmation dialog when deleting a RECURRING task — without it the
          single delete just removes the current occurrence and the recurring
          rule re-creates it the next time the parent is completed, so the
          task appears to "come back". */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Șterge task recurent"
      >
        {deleteConfirm && (
          <div className="space-y-4" data-testid="delete-recurring-modal">
            <p className="text-sm text-zinc-300">
              Task-ul <span className="font-semibold text-zinc-100">&bdquo;{deleteConfirm.task.title}&rdquo;</span> este
              recurent. Ce vrei să faci?
            </p>
            <div className="flex flex-col gap-2">
              <button
                data-testid="delete-only-this"
                onClick={() => performDelete("single")}
                className="w-full rounded-xl border border-zinc-700 bg-zinc-900 px-4 py-3 text-left transition hover:border-zinc-500 hover:bg-zinc-800"
              >
                <div className="text-sm font-medium text-zinc-100">Doar acest task</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Șterge doar ocurența curentă. Ocurențele viitoare vor continua să apară.
                </div>
              </button>
              <button
                data-testid="delete-this-and-future"
                onClick={() => performDelete("series")}
                className="w-full rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-left transition hover:border-rose-500/60 hover:bg-rose-500/20"
              >
                <div className="text-sm font-medium text-rose-200">Acest task și toate ocurențele viitoare</div>
                <div className="text-xs text-rose-300/70 mt-0.5">
                  Oprește definitiv seria — task-ul nu va mai reapărea în viitor.
                </div>
              </button>
            </div>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="w-full rounded-lg border border-zinc-700 px-4 py-2 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
            >
              Anulează
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
